import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { AuthApiError, login } from '../../auth/authApi'
import type { AuthResponse } from '../../auth/types'
import { Button, Field } from '../../components/ui'
import { AuthLayout } from './AuthLayout'
import { GoogleAuthFlow } from './GoogleAuthFlow'

type AuthContext = 'zid-oauth' | 'salla-oauth' | 'amazon-oauth' | 'zoho-oauth'

interface LoginPageProps {
  authContext?: AuthContext
  onLoginSuccess: (response: AuthResponse) => void
  onRegister: () => void
  onForgotPassword: () => void
  onHome?: () => void
}

function contextMessageKey(authContext: AuthContext): string {
  if (authContext === 'zid-oauth') return 'auth.login.contexts.zidOAuth'
  if (authContext === 'salla-oauth') return 'auth.login.contexts.sallaOAuth'
  if (authContext === 'amazon-oauth') return 'auth.login.contexts.amazonOAuth'
  return 'auth.login.contexts.zohoOAuth'
}

export function LoginPage({ authContext, onLoginSuccess, onRegister, onForgotPassword, onHome }: LoginPageProps) {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [passwordExpired, setPasswordExpired] = useState(false)
  const [loading, setLoading] = useState(false)
  const [googleActive, setGoogleActive] = useState(false)

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError(null)
    setPasswordExpired(false)

    if (!email.trim() || !password) {
      setError(t('auth.login.errors.missingFields'))
      return
    }

    setLoading(true)
    try {
      const response = await login({ email: email.trim(), password })
      onLoginSuccess(response)
    } catch (caught) {
      if (caught instanceof AuthApiError) {
        if (caught.code === 'PASSWORD_EXPIRED') {
          setPasswordExpired(true)
          setError(t('auth.login.errors.expired'))
        } else if (caught.code === 'INVALID_CREDENTIALS' || caught.code === 'UNAUTHORIZED') {
          setError(t('auth.login.errors.invalidCredentials'))
        } else {
          setError(t('auth.login.errors.network'))
        }
      } else {
        setError(t('auth.login.errors.network'))
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthLayout
      eyebrow={t('auth.login.eyebrow')}
      title={t('auth.login.title')}
      heroTitle={t('auth.login.hero.title')}
      heroSubtitle={t('auth.login.hero.subtitle')}
      onHome={onHome}
      footer={(
        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, alignItems: 'center', color: 'var(--fg-muted)', fontSize: 13 }}>
          <span>{t('auth.login.newToTlaaq')}</span>
          <button type="button" onClick={onRegister} style={{ border: 'none', background: 'transparent', color: 'var(--brand-700)', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>
            {t('auth.login.createAccount')}
          </button>
        </div>
      )}
    >
      <GoogleAuthFlow intent="login" onSuccess={onLoginSuccess} onActiveChange={setGoogleActive} disabled={loading} />
      {!googleActive && <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 16 }}>
        {authContext && (
          <div style={{ padding: '12px 14px', borderRadius: 12, background: 'var(--brand-100)', border: '1px solid var(--brand-300)', color: 'var(--brand-900)', fontSize: 13.5, lineHeight: 1.5, fontWeight: 700 }}>
            {t(contextMessageKey(authContext))}
          </div>
        )}

        <Field label={t('auth.login.email')} type="email" placeholder={t('auth.login.emailPlaceholder')} value={email} onChange={event => setEmail(event.target.value)} />
        <Field label={t('auth.login.password')} type="password" placeholder={t('auth.login.passwordPlaceholder')} value={password} onChange={event => setPassword(event.target.value)} />

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onForgotPassword} style={{ border: 'none', background: 'transparent', color: 'var(--brand-700)', fontSize: 12.5, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>
            {t('auth.login.forgot')}
          </button>
        </div>

        {error && (
          <div style={{ padding: '10px 12px', borderRadius: 10, background: passwordExpired ? 'var(--warning-100)' : 'var(--danger-100)', color: passwordExpired ? 'var(--ink-800)' : 'var(--danger)', fontSize: 13, lineHeight: 1.45 }}>
            <div>{error}</div>
            {passwordExpired && (
              <button type="button" onClick={onForgotPassword} style={{ border: 'none', background: 'transparent', color: 'var(--brand-700)', fontSize: 13, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', padding: '8px 0 0' }}>
                {t('auth.login.errors.sendReset')}
              </button>
            )}
          </div>
        )}

        <Button type="submit" size="lg" disabled={loading} style={{ justifyContent: 'center', width: '100%' }}>
          {loading ? t('auth.login.submitting') : t('auth.login.submit')}
        </Button>
      </form>}
    </AuthLayout>
  )
}
